import React, { useState, useContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { InfoIcon } from '../InfoIcon/InfoIcon'
import { useLazyRender } from '../../features/useLazyRender'

const AccordionContext = React.createContext(null)

/*
 * Bronson Accordion component.
 */
export function Accordion({
  children,
  className,
  // disableJsFx,
  headerSplit,
  itemKeyProp,
  lazyRender = false,
  multipleOpen,
  onChange,
  withBackground,
  testId,
  ...otherProps
}) {
  const [open, setOpen] = useState([])

  const { isRendered, setRendered } = useLazyRender(lazyRender)

  const isOpen = (itemKey) => {
    return open.indexOf(itemKey) >= 0
  }

  const setOpenAndNotify = (items) => {
    setOpen(items)
    onChange && onChange(items)
  }

  const updateOpen = (itemKey, shouldOpen) => {
    const index = open.findIndex((entry) => entry === itemKey)
    if (multipleOpen) {
      // is not yet open + shouldOpen -> open
      if (index < 0 && shouldOpen) {
        setOpenAndNotify([...open, itemKey])
      } else if (index >= 0 && !shouldOpen) {
        // is open + !shouldOpen -> close
        open.splice(index, 1)
        setOpenAndNotify([...open])
      }
    } else if (shouldOpen) {
      // is not open -> open
      setOpenAndNotify([itemKey])
    } else {
      // is open -> close
      setOpenAndNotify([])
    }
    if (shouldOpen) {
      setRendered(itemKey)
    }
  }

  /*
   * Change open state for the given item (ref) without the need to know the current state.
   */
  const toggleOpen = (itemKey) => {
    updateOpen(itemKey, !isOpen(itemKey))
  }

  const divClassNameList = classNames(
    'c-accordion',
    'js-accordion',
    {
      'js-is-fx': false, // && !disableJsFx, // Currently generally disabled.
      // Main class modifier convenience prop extension.
      'c-accordion--bg-panel': withBackground, // Convenience prop from Bronson variants.
      'c-accordion--header-split': headerSplit, // Convenience prop from Bronson variants.
    },
    className
  ).trim()

  return (
    <div
      {...otherProps}
      data-testid={testId}
      className={divClassNameList}
      data-accordion-multiple={!multipleOpen && 'true'}
    >
      <AccordionContext.Provider value={{ isOpen, toggleOpen, updateOpen, itemKeyProp, isRendered }}>
        {children}
      </AccordionContext.Provider>
    </div>
  )
}

Accordion.propTypes = {
  className: PropTypes.string, // Bronson template: 'accordion-modifier'.
  multipleOpen: PropTypes.bool, // Bronson template: 'group'.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
  // disableJsFx: PropTypes.bool, // Bronson template: 'disable-js-fx'.
  /* Convenience props. */
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]), // Use {@link AccordionItem}.
  itemKeyProp: PropTypes.string, // Property to identify the items, by default 'title' (hint: this is used to notify about changes, see onChange).
  lazyRender: PropTypes.bool, // Enables conditional rendering of hidden elements to improve performance in case of complex children, default is false.
  onChange: PropTypes.func, // Function called when the open state of the Accordion changes, all opened itemKeys are passed to the function.
  withBackground: PropTypes.bool, // Convenience prop for `.c-accordion--bg-panel`. Bronson template: 'accordion-modifier'.
}

/*
 * Bronson AccordionItem component.
 */
export function AccordionItem(props) {
  const {
    children,
    className,
    contentClassName,
    defaultOpen,
    icon,
    subtitle,
    title,
    titleSplit,
    tooltip,
    testId,
    ...otherProps
  } = props
  /**
   * Track onAnimationEnd expanded/collapsed state.
   */
  const [isExpanded, setIsExpanded] = useState(false)
  const context = useContext(AccordionContext)
  const itemKey = context.itemKeyProp ? props[context.itemKeyProp] : title

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    context.updateOpen(itemKey, defaultOpen)
  }, [defaultOpen])

  const childDivClassNameList = classNames(
    {
      'c-accordion__header ': true,
      'js-accordion__title ': true,
      'is-active': context.isOpen(itemKey),
    },
    className
  ).trim()

  const contentDivClassNameList = classNames('c-accordion__content', contentClassName).trim()
  const panelClassNameList = classNames('c-accordion__panel', 'js-accordion__panel', {
    'is-closed': !context.isOpen(itemKey),
    /**
     * Async because triggered via onAnimationEnd.
     * Make sure the async state classes are just set according
     * to the {@link context.isOpen} state. `.is-expanded` is only applied
     * after expanding the panel, `.is-collapsed` is only applied after
     * collapsing it.
     */
    'is-collapsed': !context.isOpen(itemKey) && !isExpanded,
    'is-expanded': context.isOpen(itemKey) && isExpanded,
  }).trim()

  return (
    <>
      {/* The `<div>` element has a child `<span>` element that allows keyboard interaction. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className={childDivClassNameList}
        onClick={() => {
          context.toggleOpen(itemKey)
          /**
           * Remove the expanded state directly onclick when the accordion is closing.
           */
          if (!context.isOpen(itemKey)) {
            setIsExpanded(false)
          }
        }}
        data-testid={testId}
        {...otherProps}
      >
        <span className="c-accordion__title-icon" />
        <h3 className="c-accordion__title">
          <span
            className="c-accordion__title-label js-accordion__title-label"
            aria-expanded={context.isOpen(itemKey)}
            role="button"
            tabIndex="0"
          >
            <span className="c-accordion__title-label-text">
              {title}
              {icon && (
                <InfoIcon className="c-accordion__title-info-icon" icon={icon}>
                  {tooltip}
                </InfoIcon>
              )}
            </span>
            {titleSplit && <span className="c-accordion__title-label-text">{titleSplit}</span>}
          </span>
        </h3>
        {subtitle && <p className="c-accordion__subtitle">{subtitle}</p>}
      </div>
      {context.isRendered(itemKey) && (
        /**
         * Need to pass `[inert=""]` as React currently does not support HTMLElement.inert mapping.
         * @see https://github.com/facebook/react/pull/24730
         * @TODO: Remove once React supports direct `[inert]` usage.
         */
        <div
          className={panelClassNameList}
          aria-hidden={!context.isOpen(itemKey)}
          inert={context.isOpen(itemKey) ? null : ''}
          onAnimationStart={(event) => {
            event.persist()
          }}
          onAnimationEnd={(event) => {
            event.persist()
            setIsExpanded(context.isOpen(itemKey))
          }}
        >
          <div className={contentDivClassNameList}>{children}</div>
        </div>
      )}
    </>
  )
}

Accordion.Item = AccordionItem
Accordion.Item.displayName = 'Accordion.Item'

Accordion.Item.propTypes = {
  children: PropTypes.node, // Placed inside `.c-accordion__content`. Bronson template: 'content'.
  className: PropTypes.string, // Custom CSS classes on `.c-accordion__header`.
  contentClassName: PropTypes.string, // Custom CSS classes on `.c-accordion__content`.
  defaultOpen: PropTypes.bool, // Set if the item should be open by default.
  icon: PropTypes.string, // Bronson template: 'icon'.
  subtitle: PropTypes.node, // Bronson template: 'subtitle'.
  title: PropTypes.node, // Bronson template: 'title'.
  titleSplit: PropTypes.node, // Bronson template: 'title-split'.
  tooltip: PropTypes.node, // Bronson template: 'tooltip'.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
}
